import { useCallback, useEffect, useState, type RefObject } from 'react';
import { pageDimsFromSection } from '@/lib/docx/page-metrics';

/** One rendered page, positioned in the overlay layer's coordinate space. */
export interface DocxPageGeometry {
  /** 1-indexed. */
  pageNumber: number;
  /** Offset from the positioned container, in CSS px (already scaled). */
  left: number;
  top: number;
  /** Scaled page box in CSS px. */
  width: number;
  height: number;
  /**
   * What the section actually renders as, in scaled CSS px.
   *
   * Differs from `height` when the section overflows its `min-height` — docx-preview
   * splits pages only on Word's cached break markers, which go stale after a merge or
   * an edit. Anything below the nominal boundary then maps past the page bottom, so
   * this is worth reporting even though the mapping deliberately ignores it.
   */
  measuredHeight: number;
  /** True page size in PDF points. */
  widthPt: number;
  heightPt: number;
}

interface Options {
  /** The docx-preview output root. */
  docRef: RefObject<HTMLElement | null>;
  /** The positioned element the overlay layer is absolute within. */
  containerRef: RefObject<HTMLElement | null>;
  /** Bumped after each (re-)render so geometry is recomputed. */
  layoutVersion: number;
  appliedScale: number;
  /** Skip while a render is in flight. */
  rendering: boolean;
}

/**
 * Measures each rendered `.docx` page so overlays can be positioned over it.
 *
 * Page identity is stable across edits: docx-preview never repaginates — it
 * splits only on cached `w:lastRenderedPageBreak` markers, explicit breaks, and
 * section-property changes — and applying a text edit rewrites `<w:t>` content
 * without touching any of those. So a field pinned to page 3 stays on page 3.
 * (Whether that matches the *converted PDF's* pagination is a separate problem,
 * handled at send time by re-detecting anchors.)
 */
export function useDocxPageGeometry({
  docRef,
  containerRef,
  layoutVersion,
  appliedScale,
  rendering,
}: Options): DocxPageGeometry[] {
  const [pages, setPages] = useState<DocxPageGeometry[]>([]);

  const measure = useCallback(() => {
    const root = docRef.current;
    const container = containerRef.current;
    if (!root || !container || rendering) {
      setPages((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const sections = Array.from(root.querySelectorAll<HTMLElement>('section.docxpv'));
    const containerRect = container.getBoundingClientRect();
    const scale = appliedScale || 1;

    const next = sections.map((section, index) => {
      const rect = section.getBoundingClientRect();
      const { widthPt, heightPt: nominalHeightPt } = pageDimsFromSection(section, scale);

      // Width is the trustworthy axis — docx-preview's inline `width` survives
      // untouched — so the points-per-pixel factor is derived from it alone.
      // Both axes share this factor, so x and y stay on the same scale.
      const pxPerPt = rect.width / widthPt;

      // Cover the page as RENDERED, never just its nominal box. Sections are laid
      // out with `min-height`, so content can push one past that box; and the
      // inline min-height can be overwritten after creation, which reads back as
      // a page a few points tall. Either way the user drags against what they
      // see, and a short host clips the bottom of the page out of reach.
      const height = Math.max(nominalHeightPt * pxPerPt, rect.height);

      return {
        pageNumber: index + 1,
        left: rect.left - containerRect.left,
        top: rect.top - containerRect.top,
        width: rect.width,
        height,
        measuredHeight: rect.height,
        widthPt,
        // Kept consistent with `height` so the drag clamp reaches the page
        // bottom. Positions are unaffected: the render maps y through
        // `width / widthPt`, in which heightPt cancels out.
        heightPt: height / pxPerPt,
      };
    });

    setPages(next);
  }, [docRef, containerRef, appliedScale, rendering]);

  useEffect(() => {
    measure();
  }, [measure, layoutVersion]);

  // Reflows that don't bump layoutVersion (container resize, font swap).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, measure]);

  return pages;
}
